import React, { useState, useEffect } from 'react';
import { firestoreService } from '../../shared/services/firestoreService';
import { WaveIcon, StampIcon, SmartphoneIcon, FlaskIcon, CheckIcon } from './icons/UiIcons';

interface OnboardingWizardProps {
  restaurantId: string;
  restaurantName: string;
  onComplete: () => void;
}

type Step = 'welcome' | 'program' | 'qr' | 'test' | 'staff';
const STEPS: Step[] = ['welcome', 'program', 'qr', 'test', 'staff'];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ restaurantId, restaurantName, onComplete }) => {
  const [currentStep, setCurrentStep] = useState<Step>('welcome');
  const [programType, setProgramType] = useState<'points' | 'stamps'>('stamps');
  const [targetCount, setTargetCount] = useState(10);
  const [rewardLabel, setRewardLabel] = useState('');
  const [welcomeBonus, setWelcomeBonus] = useState(0);
  const [staffEmail, setStaffEmail] = useState('');
  const [staffInvited, setStaffInvited] = useState<string[]>([]);
  // Première puce NFC : adresse signée fabriquée par le serveur (generateNfcBatch), jamais
  // dans le navigateur. L'ancien jeton qrToken (lisible par tous) n'existe plus.
  const [tagUrl, setTagUrl] = useState('');
  const [tagBusy, setTagBusy] = useState(false);
  const [tagError, setTagError] = useState('');
  const [tagCopied, setTagCopied] = useState(false);
  const [testDone, setTestDone] = useState(false);
  const [saving, setSaving] = useState(false);

  const stepIndex = STEPS.indexOf(currentStep);
  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  useEffect(() => {
    if (currentStep !== 'qr' || tagUrl) return;
    // Puce déjà créée (assistant relancé) : on la réaffiche plutôt que d'en créer une autre.
    firestoreService.getNfcTokens(restaurantId)
      .then((tags) => {
        const active = tags.find((t) => !t.consumed);
        if (active) setTagUrl(active.url);
      })
      .catch(() => { /* affichage du bouton de création */ });
  }, [currentStep, restaurantId, tagUrl]);

  const createFirstTag = async () => {
    if (tagBusy) return;
    setTagBusy(true);
    setTagError('');
    try {
      const { tokens } = await firestoreService.generateNfcBatch(1, restaurantId, 'Comptoir');
      setTagUrl(tokens[0]?.url ?? '');
    } catch (e: any) {
      setTagError(e?.message ?? 'Création impossible. Vous pourrez le faire plus tard dans « Puces NFC ».');
    } finally {
      setTagBusy(false);
    }
  };

  const copyTagUrl = async () => {
    try {
      await navigator.clipboard.writeText(tagUrl);
      setTagCopied(true);
    } catch {
      setTagCopied(false);
    }
  };

  const saveProgram = async () => {
    setSaving(true);
    try {
      const config = {
        type: programType,
        targetCount: programType === 'stamps' ? targetCount : null,
        spendingRatio: programType === 'points' ? 1 : null,
        targetSpending: null,
        welcomeBonus,
        rewardLabel: rewardLabel || (programType === 'stamps' ? 'Plat offert' : 'Remise fidélité'),
        missions: [],
      };
      await firestoreService.saveLoyaltyProgram(restaurantId, config);
      setCurrentStep('qr');
    } catch (err: any) {
      console.error('Erreur sauvegarde programme:', err);
      alert('Erreur lors de la sauvegarde. Réessayez.');
    } finally {
      setSaving(false);
    }
  };

  const inviteStaff = async () => {
    if (!staffEmail.trim()) return;
    const email = staffEmail.trim().toLowerCase();
    if (staffInvited.includes(email)) return;

    const staffData = {
      restaurantId,
      email,
      firstName: email.split('@')[0],
      lastName: '',
      role: 'STAFF' as const,
      isActive: true,
    };

    await firestoreService.inviteStaff(staffData);
    setStaffInvited([...staffInvited, email]);
    setStaffEmail('');

    const subject = encodeURIComponent(`Rejoignez ${restaurantName} sur ATABLE`);
    const body = encodeURIComponent(
      `Bonjour,\n\nVous êtes invité(e) à rejoindre ${restaurantName} sur ATABLE.\nConnectez-vous avec votre email : ${email}\n\nÀ bientôt !`
    );
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      await firestoreService.updateRestaurantProfile(restaurantId, { onboardingCompleted: true } as any);
      onComplete();
    } catch (err: any) {
      console.error('Erreur finalisation:', err);
      alert('Erreur lors de la finalisation. Réessayez.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">

        {/* Progress bar */}
        <div className="h-2 bg-gray-100">
          <div className="h-full bg-indigo-600 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-4 flex justify-between text-xs text-gray-400">
          {STEPS.map((s, i) => (
            <span key={s} className={i <= stepIndex ? 'text-indigo-600 font-bold' : ''}>
              {i + 1}
            </span>
          ))}
        </div>

        {/* ── WELCOME ── */}
        {currentStep === 'welcome' && (
          <div className="p-8 text-center space-y-6">
            <div className="w-20 h-20 bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary rounded-full flex items-center justify-center mx-auto">
              <WaveIcon className="w-10 h-10" />
            </div>
            <h2 className="text-2xl font-extrabold text-gray-900">
              Bienvenue sur atable, {restaurantName} !
            </h2>
            <p className="text-gray-500">
              En 5 étapes simples, vous allez configurer votre programme de fidélité,
              générer votre QR code, et inviter votre équipe. Moins de 10 minutes.
            </p>
            <button
              onClick={() => setCurrentStep('program')}
              className="w-full py-4 bg-indigo-600 text-white font-bold rounded-2xl shadow-lg hover:bg-indigo-700 transition-colors"
            >
              C'est parti !
            </button>
          </div>
        )}

        {/* ── PROGRAM ── */}
        {currentStep === 'program' && (
          <div className="p-6 space-y-5">
            <h2 className="text-xl font-extrabold text-gray-900">Votre programme fidélité</h2>
            <p className="text-sm text-gray-500">Choisissez le type qui correspond à votre établissement.</p>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setProgramType('stamps')}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  programType === 'stamps' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <StampIcon className="w-6 h-6 text-brand-primary" />
                <p className="font-bold mt-1">Tampons</p>
                <p className="text-xs text-gray-400">X visites = 1 récompense</p>
              </button>
              <button
                onClick={() => setProgramType('points')}
                className={`p-4 rounded-2xl border-2 text-left transition-all ${
                  programType === 'points' ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <svg className="w-6 h-6 text-brand-primary" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
                <p className="font-bold mt-1">Points</p>
                <p className="text-xs text-gray-400">Cumuler des points à chaque visite</p>
              </button>
            </div>

            {programType === 'stamps' && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Nombre de visites pour la récompense
                </label>
                <input
                  type="number"
                  min={3}
                  max={20}
                  value={targetCount}
                  onChange={e => setTargetCount(Number(e.target.value))}
                  className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 focus:border-indigo-500 outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>
            )}

            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Nom de la récompense
              </label>
              <input
                type="text"
                value={rewardLabel}
                onChange={e => setRewardLabel(e.target.value)}
                placeholder={programType === 'stamps' ? 'Ex: Un dessert offert' : 'Ex: 10% de remise'}
                className="w-full border-2 border-gray-400 rounded-xl px-4 py-3 focus:border-indigo-500 outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setCurrentStep('welcome')}
                className="px-6 py-3 text-gray-500 hover:text-gray-700"
              >
                Retour
              </button>
              <button
                onClick={saveProgram}
                disabled={saving}
                className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-lg disabled:opacity-50"
              >
                {saving ? 'Enregistrement...' : 'Suivant'}
              </button>
            </div>
          </div>
        )}

        {/* ── QR CODE ── */}
        {currentStep === 'qr' && (
          <div className="p-6 space-y-5 text-center">
            <h2 className="text-xl font-extrabold text-gray-900">Votre puce NFC du comptoir</h2>
            <p className="text-sm text-gray-500">
              Le client approche son téléphone de la puce pour valider son repas. Créez son adresse,
              puis programmez-la avec l'application « NFC Tools » (Écrire → URL). Sans NFC, le client
              affiche son QR et vous le scannez depuis « Encaissement fidélité ».
            </p>

            <div className="bg-gray-50 rounded-2xl p-6 space-y-3">
              <SmartphoneIcon className="w-12 h-12 mx-auto text-gray-400" />
              {tagUrl ? (
                <>
                  <input
                    readOnly
                    value={tagUrl}
                    aria-label="Adresse à programmer sur la puce du comptoir"
                    onFocus={(e) => e.currentTarget.select()}
                    className="w-full text-xs font-mono bg-white border border-gray-400 rounded-lg px-3 py-2 text-gray-700"
                  />
                  <button
                    type="button"
                    onClick={copyTagUrl}
                    className="inline-block px-6 py-2 bg-gray-200 text-gray-700 rounded-xl text-sm font-medium hover:bg-gray-300"
                  >
                    {tagCopied ? 'Adresse copiée ✓' : 'Copier l\'adresse'}
                  </button>
                  <p className="text-xs text-gray-500">D'autres puces (tables, terrasse) : menu « Puces NFC ».</p>
                </>
              ) : (
                <button
                  type="button"
                  onClick={createFirstTag}
                  disabled={tagBusy}
                  className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-xl text-sm font-bold disabled:opacity-50"
                >
                  {tagBusy ? 'Création…' : 'Créer la puce du comptoir'}
                </button>
              )}
              {tagError && <p role="alert" className="text-xs text-red-600">{tagError}</p>}
            </div>

            <div className="flex gap-3 pt-2">
              <button onClick={() => setCurrentStep('program')} className="px-6 py-3 text-gray-500 hover:text-gray-700">
                Retour
              </button>
              <button
                onClick={() => setCurrentStep('test')}
                className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-2xl"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ── TEST SANDBOX ── */}
        {currentStep === 'test' && (
          <div className="p-6 space-y-5 text-center">
            <h2 className="text-xl font-extrabold text-gray-900">Testez votre programme</h2>
            <p className="text-sm text-gray-500">
              Simulez un scan client pour vérifier que tout fonctionne.
            </p>

            {!testDone ? (
              <button
                onClick={() => setTestDone(true)}
                className="w-full py-4 bg-emerald-600 text-white font-bold rounded-2xl shadow-lg hover:bg-emerald-700 transition-colors inline-flex items-center justify-center gap-2"
              >
                <FlaskIcon className="w-5 h-5" /> Simuler un scan client
              </button>
            ) : (
              <div className="bg-emerald-50 dark:bg-emerald-500/10 rounded-2xl p-6 space-y-3">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center mx-auto">
                  <CheckIcon className="w-8 h-8" />
                </div>
                <p className="font-bold text-green-700">Simulation réussie !</p>
                <p className="text-sm text-green-600">
                  Un client fictif a validé sa visite. Vous verrez cette visite
                  dans votre dashboard après l'onboarding.
                </p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button onClick={() => setCurrentStep('qr')} className="px-6 py-3 text-gray-500 hover:text-gray-700">
                Retour
              </button>
              <button
                onClick={() => setCurrentStep('staff')}
                className="flex-1 py-3 bg-indigo-600 text-white font-bold rounded-2xl"
              >
                Suivant
              </button>
            </div>
          </div>
        )}

        {/* ── STAFF ── */}
        {currentStep === 'staff' && (
          <div className="p-6 space-y-5">
            <h2 className="text-xl font-extrabold text-gray-900">Invitez votre équipe</h2>
            <p className="text-sm text-gray-500">
              Vos serveurs pourront valider manuellement les visites en cas de problème NFC.
              Vous pouvez aussi faire ça plus tard.
            </p>

            <div className="flex gap-2">
              <input
                type="email"
                value={staffEmail}
                onChange={e => setStaffEmail(e.target.value)}
                placeholder="email@serveur.com"
                className="flex-1 border-2 border-gray-400 rounded-xl px-4 py-3 text-sm focus:border-indigo-500 outline-none focus:ring-1 focus:ring-brand-500"
                onKeyDown={e => e.key === 'Enter' && inviteStaff()}
              />
              <button
                onClick={inviteStaff}
                disabled={!staffEmail.trim()}
                className="px-4 py-3 bg-indigo-100 text-indigo-700 font-bold rounded-xl disabled:opacity-40"
              >
                Inviter
              </button>
            </div>

            {staffInvited.length > 0 && (
              <div className="space-y-2">
                {staffInvited.map(email => (
                  <div key={email} className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2 rounded-xl">
                    <CheckIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{email}</span>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 space-y-3">
              <button
                onClick={completeOnboarding}
                disabled={saving}
                className="w-full py-4 bg-indigo-600 text-white font-bold rounded-lg shadow-lg disabled:opacity-50"
              >
                {saving ? 'Finalisation...' : 'Terminer la configuration'}
              </button>
              <button
                onClick={() => setCurrentStep('test')}
                className="w-full py-2 text-sm text-gray-400 hover:text-gray-600"
              >
                Retour
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingWizard;
